// THE PER-ELEMENT LIST INSIDE THE SELECTION PANEL.
//
// Owner, 2026-09-24: "build it" - a list of the elements a box caught,
// with a tick on each row and a status control, and the window it is
// showing STATED ON THE SCREEN.
//
// WHY THE ROWS COME FROM THE SERVER AND NOT FROM THE VIEWER'S ARRAY.
// A Revit UniqueId is about 45 characters, so 41,204 of them is roughly
// 1.9 MB of strings and about 9 MB at the viewer's hit-test cap - held
// in page state to draw 500 rows. Worse than the size: the page's
// levels, its hidden documents and the project's saved scope all narrow
// on the server already, and paging from an array would mean a second
// implementation of every one of them in React. The rows the person
// reads and the set the button writes to come out of the SAME query.
//
// THE WINDOW SENTENCE IS NOT DECORATION. It is the reason a paged list
// was defensible at all: a person who ticks three rows out of a visible
// 500 must be able to see that there are 41,204 and that the button is
// about to act on all of them unless they tick.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Select, Spin, Table, Tag } from "antd";
import { popupInStage } from "./stagePopup";
import { getElementList, setElementStatus } from "api/tracking/trackingElementsApi";
import { STATUS_COLOR, STATUS_LABEL, STATUS_ORDER } from "./trackingTheme";
import { PAGE_SIZE, buttonLabel, describeTarget, windowLine, writeTarget } from "./selectionList";
import AreaTag from "./AreaTag";

const T = { muted: "#6B7280", ink: "#111827", line: "#E5E7EB" };

export default function SelectionElementList({
  projectId,
  selectionId = null,
  // SELECT-ALL-FILTERED: the filter IS the selection, and that branch
  // creates no selection document and carries no ids at all (Paul,
  // 2026-09-24). Without this the list would be empty for the largest
  // N anyone has on the page.
  filters = null,
  // The ids the page holds, used only when there is no selection
  // document to point at (a small click-selection).
  uniqueIds = null,
  documentIds = [],
  height = 320,
  onChanged = null,
  // The status control here has no gate of its own (the server decides);
  // the area tag beside it follows the same rule unless told otherwise.
  canEdit = true,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ticked, setTicked] = useState([]);
  const [status, setStatus] = useState("installed");
  const [writing, setWriting] = useState(false);
  const [note, setNote] = useState("");
  const seq = useRef(0);
  const docs = useMemo(() => (documentIds || []).join(","), [documentIds]);
  // Extracted so the dependency arrays below can be checked statically.
  const idCount = (uniqueIds || []).length;

  // A new selection is a new list: the ticks from the old one must not
  // survive it, or the button would write to rows nobody can see.
  const filterKey = useMemo(() => (filters ? JSON.stringify(filters) : ""), [filters]);
  useEffect(() => { setTicked([]); setPage(1); setNote(""); }, [selectionId, idCount, filterKey]);

  const load = useCallback(() => {
    if (!projectId || (!selectionId && !idCount && !filterKey)) { setRows([]); setTotal(0); return; }
    const mine = (seq.current += 1);
    setBusy(true);
    setError("");
    const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE, ...(docs ? { documentIds: docs } : {}) };
    if (filterKey) params.filters = filterKey;
    else if (selectionId) params.selectionId = selectionId;
    getElementList(projectId, params)
      .then((r) => {
        if (seq.current !== mine) return;
        setRows(r.elements || []);
        // THE TOTAL IS THE SERVER'S. It is the number the sentence and
        // the button are both built from, so it is never derived from
        // the rows that came back.
        setTotal(Number(r.total) || 0);
      })
      .catch((e) => { if (seq.current === mine) { setError(e.message || "Could not list the selection"); setRows([]); } })
      .finally(() => { if (seq.current === mine) setBusy(false); });
  }, [projectId, selectionId, filterKey, docs, page, idCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(load, [load]);

  const target = writeTarget({ ticked, total, selectionId, uniqueIds, filters });
  // WHAT THE NUMBER IS, IN WORDS. With a filter and nothing picked the button is
  // correctly disabled - and a dead button with no reason is its own bug, so the
  // panel says which set it would act on and what to do about it.
  const what = describeTarget(target, total);
  const line = windowLine(rows.length + (page - 1) * PAGE_SIZE, total);

  const apply = async () => {
    if (!target.count) return;
    setWriting(true);
    setNote("");
    try {
      const r = await setElementStatus(projectId, { ...target.body, status });
      setNote(`${Number(r.changed || r.count || 0).toLocaleString()} element(s) set to ${STATUS_LABEL[status] || status}`);
      setTicked([]);
      load();
      if (onChanged) onChanged(r);
    } catch (e) {
      if (!e.cancelled) setError(e.message || "Could not set the status");
    } finally { setWriting(false); }
  };

  const columns = [
    { title: "Element", dataIndex: "name", key: "name", ellipsis: true,
      render: (v, r) => <span style={{ color: T.ink }}>{v || r.typeName || r.uniqueId}</span> },
    { title: "Category", dataIndex: "category", key: "category", width: 160, ellipsis: true },
    { title: "Type", dataIndex: "typeName", key: "typeName", width: 140, ellipsis: true },
    { title: "Level", dataIndex: "level", key: "level", width: 90, ellipsis: true },
    { title: "Status", dataIndex: "status", key: "status", width: 120,
      render: (v) => <Tag color={STATUS_COLOR[v] || "default"}>{STATUS_LABEL[v] || v}</Tag> },
  ];

  if (!selectionId && !idCount && !filterKey) return null;

  return (
    <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontWeight: 600, color: T.ink }}>Elements</span>
        {/* The window, in the owner's own words. */}
        {line ? <span data-testid="window-line" style={{ color: T.muted, fontSize: 12 }}>{line}</span> : null}
        {ticked.length ? (
          <span style={{ color: T.muted, fontSize: 12 }}>{ticked.length.toLocaleString()} ticked</span>
        ) : null}
        <span style={{ flex: 1 }} />
        <Select getPopupContainer={popupInStage} value={status} onChange={setStatus} style={{ width: 150 }} size="small"
          options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] || s }))} />
        <span style={{ color: target.needsPick ? "#B45309" : T.muted, fontSize: 12 }}>{what}</span>
        <Button type="primary" size="small" loading={writing} disabled={!target.count} onClick={apply}
          data-testid="set-status">
          {buttonLabel(target.count, STATUS_LABEL[status] || status)}
        </Button>
      </div>
      {/* THE AREA, beside the status and acting on the SAME target. */}
      <div style={{ marginBottom: 8 }}>
        <AreaTag projectId={projectId} target={target} canEdit={canEdit} onTagged={(r) => { setTicked([]); load(); if (onChanged) onChanged(r); }} />
      </div>
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} /> : null}
      {note ? <Alert type="success" showIcon message={note} style={{ marginBottom: 8 }} /> : null}
      <Spin spinning={busy}>
        <Table getPopupContainer={popupInStage}
          size="small"
          rowKey="uniqueId"
          columns={columns}
          dataSource={rows}
          scroll={{ y: height }}
          rowSelection={{ selectedRowKeys: ticked, onChange: setTicked, preserveSelectedRowKeys: true }}
          pagination={total > PAGE_SIZE ? {
            current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false,
            onChange: setPage, size: "small",
          } : false}
        />
      </Spin>
    </div>
  );
}
